import * as RDialog from '@radix-ui/react-dialog'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { CornerDownLeft, Search } from 'lucide-react'
import { api } from '@/lib/api'
import { cn, useDebounced } from '@/lib/utils'
import { usePerms } from '@/lib/auth'
import type { PatientSummary } from '@/api/types'
import { PatientChip } from '@/components/domain'
import type { NavGroup } from './nav'

type Item = { kind: 'patient'; p: PatientSummary } | { kind: 'page'; label: string; to: string; group: string }

export function CommandPalette({ open, onOpenChange, nav }: { open: boolean; onOpenChange: (o: boolean) => void; nav: NavGroup[] }) {
  const [q, setQ] = useState('')
  const [active, setActive] = useState(0)
  const dq = useDebounced(q, 180)
  const navigate = useNavigate()
  const { can } = usePerms()
  const { data: patients } = useQuery({
    queryKey: ['palette-search', dq],
    queryFn: () => api.get<PatientSummary[]>('/patients/search', { q: dq, limit: 6 }),
    enabled: open && can('patients.read') && dq.trim().length >= 2,
  })
  useEffect(() => {
    if (!open) setQ('')
  }, [open])

  const items: Item[] = useMemo(() => {
    const pages = nav.flatMap((g) => g.items.map((i) => ({ kind: 'page' as const, label: i.label, to: i.to, group: g.label })))
    const extra = [
      can('patients.write') ? { kind: 'page' as const, label: 'Register new patient', to: '/app/patients/new', group: 'Actions' } : null,
      can('appointments.manage') ? { kind: 'page' as const, label: 'New walk-in visit', to: '/app/queue?walkin=1', group: 'Actions' } : null,
      can('pharmacy.dispense') ? { kind: 'page' as const, label: 'Pharmacy point of sale', to: '/app/pharmacy?tab=pos', group: 'Actions' } : null,
    ].filter(Boolean) as Item[]
    const ql = q.toLowerCase()
    const pageMatches = [...extra, ...pages].filter((p) => p.kind === 'page' && p.label.toLowerCase().includes(ql)).slice(0, 8)
    return [...(patients ?? []).map((p) => ({ kind: 'patient' as const, p })), ...pageMatches]
  }, [nav, patients, q, can])

  const go = (it: Item) => {
    onOpenChange(false)
    navigate(it.kind === 'patient' ? `/app/patients/${it.p.id}` : it.to)
  }

  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-[2px]" />
        <RDialog.Content className="fixed top-[12vh] left-1/2 z-50 w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 overflow-hidden rounded-2xl bg-surface shadow-[var(--shadow-pop)] animate-fade-in">
          <RDialog.Title className="sr-only">Search</RDialog.Title>
          <RDialog.Description className="sr-only">Search patients by name, MRN, phone or ID, or jump to a page.</RDialog.Description>
          <div className="flex items-center gap-3 border-b border-ink-100 px-4">
            <Search className="h-5 w-5 text-ink-500" />
            <input
              autoFocus
              value={q}
              onChange={(e) => {
                setQ(e.target.value)
                setActive(0)
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault()
                  setActive((a) => Math.min(a + 1, items.length - 1))
                }
                if (e.key === 'ArrowUp') {
                  e.preventDefault()
                  setActive((a) => Math.max(a - 1, 0))
                }
                if (e.key === 'Enter' && items[active]) go(items[active])
              }}
              placeholder={can('patients.read') ? 'Search patients by name, MRN, phone or ID — or type a page name' : 'Type a page name'}
              className="h-14 flex-1 bg-transparent text-[15px] outline-none placeholder:text-ink-500"
              aria-label="Search"
            />
          </div>
          <ul className="scroll-thin max-h-[60vh] overflow-y-auto p-2" role="listbox">
            {items.length === 0 && <li className="px-3 py-8 text-center text-sm text-ink-500">{dq.length >= 2 ? 'No matches.' : 'Start typing to search.'}</li>}
            {items.map((it, i) => (
              <li
                key={it.kind === 'patient' ? it.p.id : it.to + it.label}
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => go(it)}
                className={cn('flex cursor-pointer items-center justify-between gap-3 rounded-xl px-3 py-2.5', i === active && 'bg-ink-100')}
              >
                {it.kind === 'patient' ? (
                  <PatientChip p={it.p} link={false} />
                ) : (
                  <span className="text-sm">
                    <span className="text-ink-500">{it.group} › </span>
                    <span className="font-medium text-ink-900">{it.label}</span>
                  </span>
                )}
                {i === active && <CornerDownLeft className="h-4 w-4 text-ink-500" />}
              </li>
            ))}
          </ul>
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}
